import { CR_API_ENDPOINTS } from '../constants';
import {
  ClientResponse,
  LenderResponse,
  LenderPackageResponse,
  PackageLookupResult,
} from '../types/lender-packages.types';
import { apiService } from './api.service';

class LenderPackagesService {
  /**
   * Fetches all clients.
   * @returns Array of clients
   */
  async getClients(): Promise<ClientResponse[]> {
    return apiService.get<ClientResponse[]>(CR_API_ENDPOINTS.CLIENTS);
  }

  /**
   * Fetches all active lenders.
   * @returns Array of all active lenders
   */
  async getAllLenders(): Promise<LenderResponse[]> {
    return apiService.get<LenderResponse[]>(CR_API_ENDPOINTS.LENDERS);
  }

  /**
   * Fetches lenders associated with a specific client.
   * @param clientId - The UUID of the client
   * @returns Array of lenders for the given client
   */
  async getLendersByClient(clientId: string): Promise<LenderResponse[]> {
    return apiService.get<LenderResponse[]>(
      `${CR_API_ENDPOINTS.LENDERS}/by-client/${clientId}`,
    );
  }

  /**
   * Fetches packages for a specific lender with full item hierarchy.
   * @param lenderId - The UUID of the lender
   * @returns Array of packages with items and BOM items
   */
  async getPackagesByLender(lenderId: string): Promise<LenderPackageResponse[]> {
    return apiService.get<LenderPackageResponse[]>(
      `${CR_API_ENDPOINTS.LENDER_PACKAGES}/by-lender/${lenderId}`,
    );
  }

  /**
   * Resolves a package by its package_key, searching each lender in order.
   * Used by the BOM CSV re-import to restore the package a CSV was exported from.
   *
   * @param lenderIds - Lender UUIDs to search
   * @param packageKey - The package_key to look for
   * @returns Lookup result with the owning lender and its packages, or null when not found
   */
  async findPackageByKey(
    lenderIds: string[],
    packageKey: string,
  ): Promise<PackageLookupResult | null> {
    for (const lenderId of lenderIds) {
      const packages = await this.getPackagesByLender(lenderId);
      const match = packages.find(p => p.package_key === packageKey);
      if (match) {
        return { lender_id: lenderId, package: match, lender_packages: packages };
      }
    }
    return null;
  }

  /**
   * Updates whether Enphase accessory items are auto-added for a package.
   * @param packageId - The UUID of the package
   * @param autoEnphaseAccessories - New flag value
   * @returns The updated package
   */
  async updateAutoEnphaseAccessories(
    packageId: string,
    autoEnphaseAccessories: boolean,
  ): Promise<LenderPackageResponse> {
    return apiService.patch<LenderPackageResponse>(
      `${CR_API_ENDPOINTS.LENDER_PACKAGES}/${packageId}/auto-enphase-accessories`,
      { auto_enphase_accessories: autoEnphaseAccessories },
    );
  }
}

export const lenderPackagesService = new LenderPackagesService();
